import { createInstance, OptimizelyProvider } from '@optimizely/react-sdk'
import { UserProperty } from 'utils/tracking/amplitude/amplitude.interface'
import { setUserProperties } from 'utils/tracking/amplitude'
import { observer } from 'mobx-react'
import { PropsWithChildren, useState } from 'react'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { getCookie } from 'utils/cookie'
import {
  getOptimizelyTrackingAttribute,
  trackTeal,
  trackWithOptimizely
} from 'utils/tracking'
import optimizelyErrorHandler from 'utils/tracking/optimizely/errorHandler'
import { isTenantSmava } from 'utils/url'

// Relies on continous internet connection in contrast to the datafile approach
export const optimizelyClient = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
  errorHandler: optimizelyErrorHandler
})

export const TEAL_EXPERIMENT_NAME = 'alpha_ANCH_139_teal_switch_cso'
export const TEAL_DDF_TEST = 'Delta_CAT_791_DDF'
export const TEAL_BEST_OFFER_TEST = 'beta_cat_846_best_offer'
export const TEAL_THETA_TEST = 'THETA_CAT_791_DDF'

const CustomOptimizelyProvider = observer(
  ({ children }: PropsWithChildren<unknown>) => {
    const { rootStore } = useRootStore()
    const [trackInitialization, setTrackInitialization] = useState(true)

    if (!isTenantSmava()) {
      return <>{children}</>
    }

    const userId =
      getCookie('optimizelyEndUserId') ||
      rootStore.optimizely.getOptimizelyEndUserId() ||
      ''

    optimizelyClient.setUser({
      id: userId,
      attributes: { ...getOptimizelyTrackingAttribute(rootStore) }
    })

    optimizelyClient.onReady().then(() => {
      trackWithOptimizely('test_started', rootStore, {
        userFlow: 'reached',
        userType: 'landed'
      })

      const variation = optimizelyClient.getVariation(
        TEAL_EXPERIMENT_NAME,
        userId,
        { ...getOptimizelyTrackingAttribute(rootStore) }
      )

      const variationDdfTest = optimizelyClient.getVariation(
        TEAL_DDF_TEST,
        userId,
        { ...getOptimizelyTrackingAttribute(rootStore) }
      )

      const variationBestOfferTest = optimizelyClient.getVariation(
        TEAL_BEST_OFFER_TEST,
        userId,
        { ...getOptimizelyTrackingAttribute(rootStore) }
      )

      const variationThetaTest = optimizelyClient.getVariation(
        TEAL_THETA_TEST,
        userId,
        { ...getOptimizelyTrackingAttribute(rootStore) }
      )

      /* there is no way to find experiment and variation from opitmizelyEndUserId 
    for a new experiment we need to add a new event here each time if there are running experiments */

      setUserProperties({ [UserProperty.OptimizelyEndUserId]: userId })
      setUserProperties({
        [UserProperty.OptimizelyVariation]:
          variation ||
          variationDdfTest ||
          variationBestOfferTest ||
          variationThetaTest
      })
      setUserProperties({
        [UserProperty.OptimizelyUserAttributes]: {
          ...getOptimizelyTrackingAttribute(rootStore)
        }
      })

      // This Proviver component is called twice when initializing Optimzely. We only want to send the event `optimizely_initialized` the first time

      if (trackInitialization) {
        const initPathName = variationDdfTest
          ? '/kreditantrag/debtorCount'
          : '/kreditantrag/contact'

        trackTeal({
          step: initPathName,
          transaction: {
            event: 'optimizely_initialized',
            label: initPathName,
            category: 'optimizely',
            optimizely: {
              ...(variation && {
                alpha: {
                  experimentVariationKey: `${TEAL_EXPERIMENT_NAME} | ${variation}`,
                  userId
                }
              }),
              ...(variationDdfTest && {
                delta: {
                  experimentVariationKey: `${TEAL_DDF_TEST} | ${variationDdfTest}`,
                  userId
                }
              }),
              ...(variationBestOfferTest && {
                beta: {
                  experimentVariationKey: `${TEAL_BEST_OFFER_TEST} | ${variationBestOfferTest}`,
                  userId
                }
              }),
              ...(variationThetaTest && {
                theta: {
                  experimentVariationKey: `${TEAL_THETA_TEST} | ${variationThetaTest}`,
                  userId
                }
              })
            }
          }
        })
        setTrackInitialization(false)
      }
    })

    return (
      <OptimizelyProvider optimizely={optimizelyClient}>
        {children}
      </OptimizelyProvider>
    )
  }
)

export default CustomOptimizelyProvider
